import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Host,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import {
  GetRowDataResult,
  GRID_ROW_DATA,
  GridRowDataProvider,
} from '@assetaccountant/core/directives/grid/grid-row-data.provider';
import { getWidthOfCell } from '@assetaccountant/core/directives/grid/value-format.directive';
import { ObserveResizeEvent } from '@assetaccountant/core/directives/observe-resize.directive';
import { VirtualForConfig } from '@assetaccountant/core/directives/virtual-config.directive';
import { isNotNull } from '@assetaccountant/core/utils/filter-nulls';
import { PartialObserver, Subject, SubscriptionLike } from 'rxjs';
import { buffer, debounceTime, delay, map, startWith, throttleTime } from 'rxjs/operators';
import { GridColumnGroupComponent } from '../grid-column-group/grid-column-group.component';
import { GridColumnComponent } from '../grid-column/grid-column.component';
import { GridRowClassRule } from '../grid.utils';

export interface GridRowGroup<T> {
  key: string;
  groupData?: T;
  children?: ReadonlyArray<GridRowNode<T>>;
  open: boolean;
  count: number | null;
  selected?: boolean | 'INDETERMINATE';
  openRowCssClasses?: string;
  closedRowCssClasses?: string;
}

export interface GridRowLoaded<T> {
  data: T;
  groupKey?: string;
  groupIndex: number;
  state: 'LOADED';
  selected?: boolean;
  rowCssClasses?: string;
}

export interface GridRowPending {
  data?: undefined;
  groupKey?: string;
  groupIndex: number;
  state: 'LAZY' | 'LOADING';
  selected?: boolean;
}

export interface GridRowError {
  data?: undefined;
  groupKey?: string;
  groupIndex: number;
  state: 'ERROR';
  selected?: boolean;
  requestTop: number;
}

export type GridRow<T> = GridRowLoaded<T> | GridRowPending | GridRowError;

export type GridRowNode<T> = GridRowGroup<T> | GridRow<T>;

type GroupVisibility = Record<
  string,
  {
    minIndex: number;
    maxIndex: number;
  }
>;

export const isGridRowGroup = <T>(row: GridRowNode<T>): row is GridRowGroup<T> => 'key' in row;

interface GridColumnGroupSpan<T extends {}> {
  group: GridColumnGroupComponent<T> | null;
  openColspan: number;
  closedColspan: number;
  leftOffset: number | null;
  leftFixed: number | null;
  leftNonFixed: number | null;
  rightOffset: number | null;
  rightFixed: number | null;
  rightNonFixed: number | null;
}

type SelectedRow<T> = { type: 'GROUP'; key?: string } | { type: 'ROW'; data: T };

const LAZY_LOAD_THRESHOLD = 50;
const SELECT_COLUMN_WIDTH = 30;

@Component({
  selector: 'app-grid-table',
  templateUrl: './grid-table.component.html',
  styleUrls: ['./grid-table.component.scss'],
  standalone: false,
})
export class GridTableComponent<T extends {}> implements AfterContentInit, OnInit, OnDestroy {
  readonly isGridRowGroup = isGridRowGroup;

  @Input() canSelectRows = false;
  @Input() showGroupFooter = false;
  @Input() showFooter = false;
  @Input() rowHeight: number | ((row?: T) => number) | null = null;
  @Input() rowClassRules?: Record<string, GridRowClassRule<T>>;

  @Output() rowGroupExpandCollapse = new EventEmitter<GridRowGroup<T>>();
  @Output() onSelectionChanged = new EventEmitter<T[]>();

  @ViewChild('Select', { read: TemplateRef }) selectTemplate: TemplateRef<{
    row: GridRowNode<T>;
  }>;
  @ViewChild('SelectHeader', { read: TemplateRef }) selectHeaderTemplate: TemplateRef<{}>;

  @ContentChildren(forwardRef(() => GridColumnComponent), { descendants: true }) columns: QueryList<
    GridColumnComponent<T, any>
  >;
  @ContentChildren(forwardRef(() => GridColumnGroupComponent)) columnGroups: QueryList<
    GridColumnGroupComponent<T>
  >;

  allRowsSelected: boolean | 'INDETERMINATE' = false;

  private selectColumn?: GridColumnComponent<T, any>;

  visibleColumns: Array<GridColumnComponent<T, any>> | null = null;
  columnGroupsWithSpan: Array<GridColumnGroupSpan<T>> | null = null;
  offsets: Map<GridColumnComponent<T, any>, { left: number | null; right: number | null }> =
    new Map();

  groupedData: ReadonlyArray<GridRowNode<T>> = [];
  count: number | null = null;
  get loadingColspan() {
    return this.visibleColumns ? this.visibleColumns.filter(c => !c.hidden).length : null;
  }

  openColumnWidths: number[] = [];
  closedColumnWidths: number[] = [];
  totalWidth: number | null = null;
  headHeight = 29;
  refreshing = true;
  hideNonAggColumns = false;

  get gridTemplateColumns() {
    if (!this.visibleColumns) {
      return null;
    }

    const result = this.visibleColumns
      .map((c, i) => {
        if (c.hidden || (this.hideNonAggColumns && !c.isHeader && !c.hasAggregate)) {
          return null;
        }

        const colWidth = c.group?.open ? this.openColumnWidths[i] : this.closedColumnWidths[i];

        if (c.maxWidth) {
          if (colWidth === c.maxWidth) {
            return `${colWidth || 0}px`;
          }
          return `minmax(${colWidth || 0}px, ${c.maxWidth}px)`;
        }

        return `minmax(${colWidth || 0}px, ${c.grow ?? 1}fr)`;
      })
      .filter(isNotNull)
      .join(' ');

    return result;
  }

  get standardRowHeight() {
    return (typeof this.rowHeight === 'function' ? this.rowHeight() : this.rowHeight) ?? 44;
  }

  getRowGroupKey?: (row: T) => any[];

  virtualizeConfig: VirtualForConfig<GridRowNode<T>> & { refresh$: Subject<void> };

  private visibleRows$ = new Subject<Array<GridRowNode<T>>>();

  private _subscriptions: SubscriptionLike[] = [];

  constructor(
    private cd: ChangeDetectorRef,
    @Inject(GRID_ROW_DATA) @Host() public rowDataProvider: GridRowDataProvider<T>,
  ) {
    this.virtualizeConfig = {
      itemSize: row => this.getRowHeight(row),
      visibleRows: rows => this.visibleRows$.next(rows),
      refresh$: new Subject(),
    };
  }

  ngOnInit() {
    this._subscriptions.push(
      this.visibleRows$
        .pipe(
          buffer(
            this.visibleRows$.pipe(
              this.rowDataProvider.infiniteScroll
                ? throttleTime(600, undefined, { leading: false, trailing: true })
                : debounceTime(600),
            ),
          ),
          map(visibleRows =>
            visibleRows.reduce(
              (acc, rows) => ({
                ...acc,
                ...this.applyRowIntersect(rows),
              }),
              {} as GroupVisibility,
            ),
          ),
        )
        .subscribe(groups => {
          this.processRowIntersect(groups);
        }),
      this.rowGroupExpandCollapse.pipe(debounceTime(10)).subscribe(() => {
        const newHideNonAggColumns = this.groupedData.every(isClosedRowGroupUsingAggregate);

        if (this.hideNonAggColumns !== newHideNonAggColumns) {
          this.hideNonAggColumns = newHideNonAggColumns;
          this.calculateTotalWidth();
          this.cd.detectChanges();
        }

        this.virtualizeConfig.refresh$.next();

        function isClosedRowGroupUsingAggregate(row: GridRowNode<T>) {
          if (!isGridRowGroup(row) || row.groupData) {
            return false;
          }

          if (!row.open) {
            return true;
          }

          return row.children?.every(isClosedRowGroupUsingAggregate) ?? false;
        }
      }),
    );
  }

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  ngAfterContentInit() {
    this._subscriptions.push(
      this.columnGroups.changes.pipe(delay(1)).subscribe(() => this.updateColumnGroups()),
      this.columns.changes.pipe(delay(1)).subscribe(() => {
        // Column settings
        const oldFirstColumn = this.columns.find(c => c.isHeader);
        if (oldFirstColumn) {
          oldFirstColumn.isHeader = false;
        }
        this.visibleColumns = this.columns.filter(c => !c.hide);

        if (this.visibleColumns.length > 0) {
          this.visibleColumns[0].isHeader = true;

          if (this.canSelectRows) {
            if (!this.selectColumn) {
              this.selectColumn = new GridColumnComponent<T, any>();
              this.selectColumn.cellClass = 'multi-select';
              this.selectColumn.cellTemplate = this.selectTemplate;
              this.selectColumn.header = { template: this.selectHeaderTemplate };
              this.selectColumn.fixed = this.visibleColumns[0].fixed;
              this.selectColumn.width = SELECT_COLUMN_WIDTH;
              this.selectColumn.maxWidth = SELECT_COLUMN_WIDTH;
              this.selectColumn.getValue = _ => null;
            }

            this.visibleColumns.unshift(this.selectColumn);
          }
        }

        // Row groups
        const rowGroupColumns = this.columns.filter(c => c.rowGroup);
        this.getRowGroupKey =
          rowGroupColumns.length > 0
            ? row => rowGroupColumns.map(c => c.valueGetter(row))
            : undefined;

        this.updateColumnGroups();

        // Column widths
        this.calculateColumnWidths();
      }),
      this.rowDataProvider.refresh.pipe(startWith(null)).subscribe(options => {
        this.updateColumnGroups();

        if (options?.purge) {
          this.groupedData = [];
        }
        this.count = null;
        this.calculateGroupedData();

        // Column widths
        this.calculateColumnWidths();
      }),
    );
    this.columnGroups.notifyOnChanges();
    this.columns.notifyOnChanges();
  }

  getRowHeight(row?: GridRowNode<T>, excludeChildren = false) {
    let innerGetRowHeight: (row?: T) => number;
    if (typeof this.rowHeight === 'function') {
      innerGetRowHeight = this.rowHeight;
    } else {
      const staticRowHeight = this.rowHeight ?? 44;
      innerGetRowHeight = _ => staticRowHeight;
    }

    if (!row) {
      return innerGetRowHeight();
    }

    if (isGridRowGroup(row)) {
      // header height
      let groupHeight = row.key ? innerGetRowHeight() : 0;

      if (row.open && !excludeChildren) {
        if (row.key && this.showGroupFooter) {
          // footer height
          groupHeight += innerGetRowHeight();
        }

        if (row.children) {
          groupHeight += row.children.reduce((a, r) => a + this.getRowHeight(r), 0);
        } else if (row.count) {
          groupHeight += row.count * innerGetRowHeight();
        }
      }

      return groupHeight;
    }

    return innerGetRowHeight('data' in row ? row.data : undefined);
  }

  trackBy: TrackByFunction<GridRowNode<T>> = (_i, row) =>
    isGridRowGroup(row) ? `GROUP_${row.key}` : `ROW_${row.groupKey}_${row.groupIndex}`;

  onHeadResize($event: ObserveResizeEvent<'offsetHeight'>) {
    this.headHeight = $event.offsetHeight;
  }

  private updateSelection() {
    let totalCount = 0;
    let selectedCount = 0;
    const selectedRows: T[] = [];

    this.groupedData.forEach(updateSelect);

    this.allRowsSelected =
      totalCount > 0 &&
      (totalCount === selectedRows.length || (selectedRows.length && 'INDETERMINATE') || false);

    this.onSelectionChanged.emit(selectedRows);

    function updateSelect(row: GridRowNode<T>) {
      if (isGridRowGroup(row)) {
        row.children?.forEach(updateSelect);
        row.selected =
          row.children?.every(c => c.selected) ||
          (row.children?.some(c => c.selected) && 'INDETERMINATE') ||
          false;
      } else {
        totalCount++;

        if (row.selected) {
          selectedCount++;
          if (row.data) {
            selectedRows.push(row.data);
          }
        }
      }
    }
  }

  onSelectedRowsToggled() {
    this.groupedData.forEach(row => this.setRowSelection(row, !this.allRowsSelected));

    this.updateSelection();
  }

  onSelectedRowToggled(row: GridRowNode<T>) {
    this.setRowSelection(row, !row.selected);
    this.updateSelection();
  }

  private setRowSelection(row: GridRowNode<T>, selected: boolean) {
    if (isGridRowGroup(row)) {
      row.children?.forEach(child => this.setRowSelection(child, selected));
    }
    row.selected = selected;
  }

  toggleColumnGroup(columnGroup: GridColumnGroupComponent<T>) {
    columnGroup.open = !columnGroup.open;

    this.calculateTotalWidth();
  }

  toggleRowGroup(rowGroup: GridRowGroup<T>) {
    rowGroup.open = !rowGroup.open;

    if (rowGroup.open) {
      this.initGroupRowData(rowGroup);
    }

    this.rowGroupExpandCollapse.emit(rowGroup);
  }

  private updateColumnGroups() {
    if (this.columns.length === 0) {
      this.columnGroupsWithSpan = null;
    } else {
      this.columnGroupsWithSpan = [];
      let currentColumnGroup: GridColumnGroupSpan<T> | undefined;
      for (const column of this.columns) {
        column.group = this.columnGroups.find(cg => cg.columns.some(c => c === column)) ?? null;

        if (column.hide) {
          continue;
        }

        if (
          !currentColumnGroup ||
          currentColumnGroup.group !== column.group ||
          column.group == null
        ) {
          const self = this;

          const group = column.group;

          const getColumns = group
            ? () =>
                this.hideNonAggColumns
                  ? group.columns.filter(c => c.isHeader || c.hasAggregate)
                  : group.columns.toArray()
            : () =>
                this.hideNonAggColumns && !column.isHeader && !column.hasAggregate ? [] : [column];

          currentColumnGroup = {
            group,
            get openColspan() {
              return getColumns().filter(c => c.columnGroupShow !== 'closed').length ?? 0;
            },
            get closedColspan() {
              return getColumns().filter(c => c.columnGroupShow !== 'open').length ?? 0;
            },
            get leftOffset() {
              return (
                getColumns().reduce(
                  (offset: number | null | undefined, c) => offset ?? self.offsets.get(c)?.left,
                  null,
                ) ??
                self.offsets.get(column)?.left ??
                null
              );
            },
            get leftFixed() {
              if (!this.group && column.isHeader) {
                return self.openColumnWidths[0];
              }
              let result = 0;
              for (const c of getColumns()) {
                if (!c.hidden && c.fixed === 'left') {
                  const index = self.visibleColumns?.indexOf(c);
                  if (index != null && index >= 0) {
                    result += c.group?.open
                      ? self.openColumnWidths[index]
                      : self.closedColumnWidths[index];
                  }
                }
              }
              return result || null;
            },
            get leftNonFixed() {
              let result = 0;
              for (const c of getColumns()) {
                if (!c.hidden && c.fixed !== 'left') {
                  const index = self.visibleColumns?.indexOf(c);
                  if (index != null && index >= 0) {
                    result += c.group?.open
                      ? self.openColumnWidths[index]
                      : self.closedColumnWidths[index];
                  }
                }
              }
              return result || null;
            },
            get rightOffset() {
              return (
                getColumns().reduce(
                  (offset: number | null, c) => self.offsets.get(c)?.right ?? offset,
                  null,
                ) ??
                self.offsets.get(column)?.right ??
                null
              );
            },
            get rightFixed() {
              let result = 0;
              for (const c of getColumns().reverse()) {
                if (!c.hidden && c.fixed === 'right') {
                  const index = self.visibleColumns?.indexOf(c);
                  if (index != null && index >= 0) {
                    result += c.group?.open
                      ? self.openColumnWidths[index]
                      : self.closedColumnWidths[index];
                  }
                }
              }
              return result || null;
            },
            get rightNonFixed() {
              let result = 0;
              for (const c of getColumns().reverse()) {
                if (!c.hidden && c.fixed !== 'right') {
                  const index = self.visibleColumns?.indexOf(c);
                  if (index != null && index >= 0) {
                    result += c.group?.open
                      ? self.openColumnWidths[index]
                      : self.closedColumnWidths[index];
                  }
                }
              }
              return result || null;
            },
          };
          this.columnGroupsWithSpan.push(currentColumnGroup);
        }
      }
    }

    this.cd.markForCheck();
  }

  private calculateGroupedData() {
    const self = this;
    const openGroupKeys = new Set(getOpenGroups(this.groupedData));

    const selected = getSelectedRows(this.groupedData);
    const selectedRows = new Set(
      selected.filter((x): x is { type: 'ROW'; data: T } => x.type === 'ROW').map(x => x.data),
    );
    const selectedGroups = new Set(
      selected
        .filter((x): x is { type: 'GROUP'; key: string } => x.type === 'GROUP')
        .map(x => x.key),
    );

    this.refreshing = true;
    this.getRowData(
      { top: 0 },
      {
        next: () => {
          applyOpenGroups(this.groupedData);
          applySelectedRows(this.groupedData);
          this.updateSelection();

          this.refreshing = false;

          this.calculateColumnWidths();
          this.calculateTotalWidth();
        },
        error: () => {
          this.groupedData
            .filter((row): row is GridRow<T> => !isGridRowGroup(row))
            .forEach(row => {
              if (
                !this.rowDataProvider.bucketSize ||
                row.groupIndex < this.rowDataProvider.bucketSize
              ) {
                Object.assign(row, { state: 'ERROR', requestTop: 0 });
              } else {
                row.state = 'LAZY';
              }
            });

          this.refreshing = false;
        },
      },
    );

    function getOpenGroups(rows: ReadonlyArray<GridRowNode<T>>): string[] {
      return rows
        .filter(isGridRowGroup)
        .filter(g => g.open)
        .flatMap(g => [g.key].concat(...getOpenGroups(g.children ?? [])));
    }

    function getSelectedRows(rows: ReadonlyArray<GridRowNode<T>>): ReadonlyArray<SelectedRow<T>> {
      return rows.flatMap(row => {
        if (isGridRowGroup(row)) {
          if (row.selected === true) {
            return [{ type: 'GROUP' as const, key: row.key ?? '' }];
          }
          if (row.children) {
            return getSelectedRows(row.children);
          }
        } else {
          if (row.selected === true && row.data) {
            return [{ type: 'ROW' as const, data: row.data }];
          }
        }

        return [];
      });
    }

    function applyOpenGroups(rows: ReadonlyArray<GridRowNode<T>>) {
      if (openGroupKeys.size === 0) {
        // Trigger downstream recalculations when there are no open groups
        self.rowGroupExpandCollapse.emit();
        return;
      }

      rows.filter(isGridRowGroup).forEach(g => {
        if (openGroupKeys.has(g.key)) {
          g.open = true;
          self.initGroupRowData(g);
          if (g.children) {
            applyOpenGroups(g.children);
          }
        }
        self.rowGroupExpandCollapse.emit(g);
      });
    }

    function applySelectedRows(rows: ReadonlyArray<GridRowNode<T>>) {
      if (selectedRows.size === 0 && selectedGroups.size === 0) {
        return;
      }

      rows.forEach(row => {
        if (isGridRowGroup(row)) {
          if (selectedGroups.has(row.key ?? '')) {
            row.selected = true;
            selectChildren(row);
          } else if (row.children) {
            applySelectedRows(row.children);
          }
        } else {
          if (row.data && selectedRows.has(row.data)) {
            row.selected = true;
          }
        }
      });
    }

    function selectChildren(group: GridRowGroup<T>) {
      group.children?.forEach(row => {
        row.selected = true;
        if (isGridRowGroup(row)) {
          selectChildren(row);
        }
      });
    }
  }

  private calculateColumnWidths() {
    this.openColumnWidths = [];
    this.closedColumnWidths = [];
    this.offsets.clear();
    if (!this.visibleColumns?.length) {
      this.totalWidth = null;
      return;
    }

    const flatten = (
      row: GridRowNode<T>,
      indent = 0,
    ): Array<{ data?: T; key?: string; indent: number }> => {
      if (isGridRowGroup(row)) {
        if (row.key) {
          return (row.children?.flatMap(r => flatten(r, indent + 2)) ?? []).concat(
            row.groupData
              ? [{ data: row.groupData, indent: indent + 1 }]
              : [{ key: row.key, indent: indent + 1 }],
          );
        } else {
          return row.children?.flatMap(r => flatten(r, indent)) ?? [];
        }
      } else if (row.data) {
        return [{ data: row.data, indent }];
      } else {
        return [];
      }
    };

    const flatData = this.groupedData?.flatMap(r => flatten(r)) ?? [];

    /*
     * Column groups need minimum widths for columns to show the header
     * This can vary if the group is open or closed, or scrolling when
     * one column is fixed.
     * e.g.
     * Open
     * |       Opening     < |
     * | Cost | Depn | Value |
     * Closed or Fixed
     * | Opening > |
     * | ++Value++ |
     */
    const groupHeaderWidths = new Map(
      this.columnGroups
        .filter(group => !!group.headerName)
        .map(group => {
          let groupHeaderWidth = getWidthOfCell(group.headerName);
          const currentColumns = this.visibleColumns?.filter(c => c.group === group) ?? [];
          const openColumnCount =
            currentColumns.filter(c => c.columnGroupShow !== 'closed').length || 1;
          const closedColumnCount =
            currentColumns.filter(c => c.columnGroupShow !== 'open').length || 1;
          const openFixedColumnCount =
            currentColumns.filter(c => c.fixed && c.columnGroupShow !== 'closed').length || 1;
          const closedFixedColumnCount =
            currentColumns.filter(c => c.fixed && c.columnGroupShow !== 'open').length || 1;

          if (openColumnCount !== closedColumnCount) {
            groupHeaderWidth += 10;
          }

          return [
            group,
            {
              open: groupHeaderWidth / openColumnCount,
              closed: groupHeaderWidth / closedColumnCount,
              openFixed: groupHeaderWidth / openFixedColumnCount,
              closedFixed: groupHeaderWidth / closedFixedColumnCount,
            },
          ] as const;
        }),
    );

    for (const column of this.visibleColumns) {
      let openColumnWidth: number;
      let closedColumnWidth: number;
      if (column.width) {
        openColumnWidth = column.width;
        closedColumnWidth = column.width;
      } else {
        const widths = flatData
          .filter(isNotNull)
          .flatMap(({ data, key, indent }) => {
            let strValue: string;
            if (data == null) {
              if (!column.isHeader || !key) {
                return [];
              }
              strValue = key.toString();
            } else {
              const rawValue = column.valueGetter(data) as any;
              strValue =
                (column.valueFormatter ? column.valueFormatter(rawValue) : rawValue)?.toString() ??
                '';
            }
            return strValue.split(/\n/g).map(value => ({ value, indent }));
          })
          .map(({ value, indent }) => getWidthOfCell(value) + (column.isHeader ? 24 * indent : 0));

        if (column.headerName) {
          widths.push(getWidthOfCell(column.headerAbbreviation || column.headerName));
        }

        openColumnWidth = closedColumnWidth = widths.reduce((a, b) => Math.max(a, b));

        if (column.group) {
          const groupWidths = groupHeaderWidths.get(column.group);
          if (groupWidths) {
            if (column.fixed) {
              openColumnWidth = Math.max(openColumnWidth, groupWidths.openFixed);
              closedColumnWidth = Math.max(closedColumnWidth, groupWidths.closedFixed);
            } else {
              openColumnWidth = Math.max(openColumnWidth, groupWidths.open);
              closedColumnWidth = Math.max(closedColumnWidth, groupWidths.closed);
            }
          }
        }
      }

      if (column.minWidth && openColumnWidth < column.minWidth) {
        openColumnWidth = column.minWidth;
      } else if (column.maxWidth && openColumnWidth > column.maxWidth) {
        openColumnWidth = column.maxWidth;
      }

      if (column.minWidth && closedColumnWidth < column.minWidth) {
        closedColumnWidth = column.minWidth;
      } else if (column.maxWidth && closedColumnWidth > column.maxWidth) {
        closedColumnWidth = column.maxWidth;
      }

      const index = this.openColumnWidths.length;

      this.openColumnWidths.push(openColumnWidth);
      this.closedColumnWidths.push(closedColumnWidth);

      const self = this;
      if (column.fixed === 'left') {
        const prevColumns = this.visibleColumns.slice(0, index).reverse();
        const prevFixedColumns = prevColumns.filter(c => c.fixed === 'left');

        if (prevFixedColumns.length === 0) {
          this.offsets.set(column, {
            right: null,
            left: 0,
          });
        } else {
          this.offsets.set(column, {
            right: null,
            get left() {
              const prevFixed = prevFixedColumns.find(c => !c.hidden);
              const prevOffset = prevFixed && self.offsets.get(prevFixed)?.left;

              if (prevFixed == null || prevOffset == null) {
                return 0;
              }

              const prevIndex = self.visibleColumns?.indexOf(prevFixed) ?? -1;
              const prevWidth = (
                prevFixed.group?.open ? self.openColumnWidths : self.closedColumnWidths
              )[prevIndex];

              return (
                prevOffset + prevWidth + (prevFixed === prevColumns.find(c => !c.hidden) ? 0 : -1)
              );
            },
          });
        }
      } else if (column.fixed === 'right') {
        const nextColumns = this.visibleColumns.slice(index + 1);
        const nextFixedColumns = nextColumns.filter(c => c.fixed === 'right');

        if (nextFixedColumns.length === 0) {
          this.offsets.set(column, {
            left: null,
            right: 0,
          });
        } else {
          this.offsets.set(column, {
            left: null,
            get right() {
              const nextFixed = nextFixedColumns.find(c => c.fixed === 'right');
              const nextOffset = nextFixed && self.offsets.get(nextFixed)?.right;

              if (nextFixed == null || nextOffset == null) {
                return 0;
              }

              const nextIndex = self.visibleColumns?.indexOf(nextFixed) ?? -1;
              const nextWidth = (
                nextFixed.group?.open ? self.openColumnWidths : self.closedColumnWidths
              )[nextIndex];

              return (
                nextOffset + nextWidth + (nextFixed === nextColumns.find(c => !c.hidden) ? 0 : -1)
              );
            },
          });
        }
      }
    }

    this.calculateTotalWidth();
  }

  private calculateTotalWidth() {
    let totalWidth = 0;
    this.visibleColumns?.forEach((c, i) => {
      if (c.group) {
        if (c.columnGroupShow === 'closed' && c.group.open) {
          return;
        }
        if (c.columnGroupShow === 'open' && !c.group.open) {
          return;
        }
      }
      if (this.hideNonAggColumns && !c.isHeader && !c.hasAggregate) {
        return;
      }
      totalWidth += c.group?.open ? this.openColumnWidths[i] : this.closedColumnWidths[i];
    });

    this.totalWidth = totalWidth;
    this.cd.detectChanges();
  }

  /**
   * Handles the browser intersection events as the table is scrolled.
   *
   * This method is triggered when the intersection state of rows changes.
   * It updates the visibility state of rows based on their intersection status.
   *
   * @param entries - An array of IntersectionObserverEntry objects representing the rows being observed.
   *
   * Updates the row state based on the intersection status of the rows.
   * Groups by group key
   * Emits asubject which can be debounced
   *
   */
  private applyRowIntersect(visibleRows: Array<GridRowNode<T>>) {
    const visibleRangeByGroup: GroupVisibility = {};
    visibleRows.forEach(row => {
      if (!isGridRowGroup(row)) {
        const key = row.groupKey ?? '';
        if (!visibleRangeByGroup[key]) {
          visibleRangeByGroup[key] = {
            minIndex: row.groupIndex,
            maxIndex: row.groupIndex,
          };
        } else if (row.groupIndex < visibleRangeByGroup[key].minIndex) {
          visibleRangeByGroup[key].minIndex = row.groupIndex;
        } else if (row.groupIndex > visibleRangeByGroup[key].maxIndex) {
          visibleRangeByGroup[key].maxIndex = row.groupIndex;
        }
      }
    });

    return visibleRangeByGroup;
  }

  /**
   * Processes the intersection of visible rows by group and fetches data for rows that are visible but not yet loaded.
   *
   * @private
   * @param visibleRowsByGroup - An object where the keys are group identifiers and the values are arrays of visible row states for each group.
   *
   * Should only be called after debouncing
   *
   */
  private processRowIntersect(visibleRangeByGroup: GroupVisibility) {
    // Get the existing group data structure for each group
    Object.entries(visibleRangeByGroup).forEach(([groupKey, visibleRange]) => {
      // Find group or child group with matching key

      const group = groupKey ? this.findGroup(groupKey) : null;

      let groupData: ReadonlyArray<GridRowNode<T>>;
      if (group) {
        if (!group.children) {
          throw new Error('Group should have been initialised');
        }
        groupData = group.children;
      } else {
        groupData = this.groupedData;
      }

      const unloadedVisibleRows = groupData.filter(
        (row): row is GridRow<T> =>
          !isGridRowGroup(row) &&
          row.state === 'LAZY' &&
          visibleRange.minIndex - LAZY_LOAD_THRESHOLD < row.groupIndex &&
          row.groupIndex < visibleRange.maxIndex + LAZY_LOAD_THRESHOLD,
      );

      const currentCount = group ? group.count : this.count;

      if (
        unloadedVisibleRows.length > 0 ||
        ((currentCount == null || groupData.length < currentCount) &&
          visibleRange.maxIndex > groupData.length - LAZY_LOAD_THRESHOLD)
      ) {
        if (currentCount == null || groupData.length < currentCount) {
          const top =
            unloadedVisibleRows.length > 0 ? unloadedVisibleRows[0].groupIndex : groupData.length;
          this.getRowData({
            group,
            top,
          });
        } else {
          let top = unloadedVisibleRows[0].groupIndex;

          if (this.rowDataProvider.bucketSize) {
            top -= top % this.rowDataProvider.bucketSize;
            const maxTop = unloadedVisibleRows[unloadedVisibleRows.length - 1].groupIndex;
            for (; top <= maxTop; top += this.rowDataProvider.bucketSize) {
              this.getRowData({
                group,
                top,
              });
            }
          } else {
            this.getRowData({
              group,
              top,
            });
          }
        }
      }
    });
  }

  private findGroup(
    groupKey: string,
    rows?: ReadonlyArray<GridRowNode<T>>,
  ): GridRowGroup<T> | undefined {
    for (const row of (rows ?? this.groupedData).filter(isGridRowGroup)) {
      if (row.key === groupKey) {
        return row;
      }

      const childResult = row.children && this.findGroup(groupKey, row.children);
      if (childResult) {
        return childResult;
      }
    }

    return undefined;
  }

  /**
   * Initializes the row data structure for a single group's lazy loaded rows.
   *
   * Creates an empty row stucture for the group.
   *
   */
  private initGroupRowData(group: GridRowGroup<T>) {
    if (group.children == null) {
      this.getRowData({ group, top: 0 });
    }
  }

  private getRowData(
    { group, top }: { group?: GridRowGroup<T> | null; top: number },
    observer?: PartialObserver<GetRowDataResult<T>>,
  ) {
    this.rowDataProvider
      .getRowData({
        parent: group ?? null,
        top,
        columns: this.columns,
      })
      .subscribe({
        next: rowDataResult => {
          const { rows, count } = rowDataResult;

          if (group && !group.children) {
            // No children - assign new rows
            group.children = rows;
            group.count = count;
          } else {
            let result = group?.children ?? this.groupedData;
            if (count != null && count < result.length) {
              result = result.slice(0, count);
            }
            // If the rows already exist in the destination then the data has been loaded into the rows
            if (!result.includes(rows[0]) && !result.includes(rows[rows.length - 1])) {
              if (result[top]) {
                // Replace rows
                result = [...result.slice(0, top), ...rows, ...result.slice(top + rows.length)];
              } else {
                // Append new rows
                result = [...result, ...rows];
              }
              if (count != null) {
                if (group) {
                  group.count = count;
                } else if (result.length === count) {
                  this.count = count;
                }
              }
            }

            result
              .slice(top + rows.length)
              .filter((row): row is GridRow<T> => !isGridRowGroup(row) && row.state === 'ERROR')
              .forEach(row => {
                row.state = 'LAZY';
              });

            if (group) {
              group.children = result;
            } else {
              this.groupedData = result;
            }
          }

          const destination = group?.children ?? this.groupedData;
          if (this.rowClassRules && destination) {
            Object.entries(this.rowClassRules).forEach(([cssClass, rule]) =>
              this.applyRowClassRule(cssClass, rule, destination),
            );
          }

          observer?.next?.(rowDataResult);
          this.virtualizeConfig.refresh$.next();
        },
        error: err => {
          if (group && !group.children) {
            // Add single error node
            group.children = [
              {
                groupKey: group.key,
                groupIndex: 0,
                state: 'ERROR' as const,
                requestTop: top,
              },
            ];
          } else {
            let result = group?.children ?? this.groupedData;
            if (top < result.length) {
              // Mark error nodes
              result
                .slice(
                  top,
                  this.rowDataProvider.bucketSize && top + this.rowDataProvider.bucketSize,
                )
                .filter(
                  (row): row is GridRowPending | GridRowError =>
                    !isGridRowGroup(row) && row.state !== 'LOADED',
                )
                .forEach(row => {
                  Object.assign(row, { state: 'ERROR', requestTop: top });
                });
            } else {
              result = [
                ...result,
                {
                  groupKey: group?.key,
                  groupIndex: result.length,
                  state: 'ERROR' as const,
                  requestTop: top,
                },
              ];
              if (group) {
                group.children = result;
              } else {
                this.groupedData = result;
              }
            }
          }

          observer?.error?.(err);
          this.virtualizeConfig.refresh$.next();
        },
        complete: () => {
          const destination = group?.children ?? this.groupedData;

          if (this.rowClassRules && destination) {
            Object.entries(this.rowClassRules).forEach(([cssClass, rule]) =>
              this.applyRowClassRule(cssClass, rule, destination),
            );
          }

          observer?.complete?.();

          this.cd.detectChanges();
        },
      });
  }

  retryFailed(errorRow: GridRowError) {
    const group = errorRow.groupKey ? this.findGroup(errorRow.groupKey) : null;

    this.getRowData({
      group,
      top: errorRow.requestTop,
    });
  }

  applyRowClassRule(
    cssClass: string,
    rule: GridRowClassRule<T>,
    rows: ReadonlyArray<GridRowNode<T>>,
  ): 'EVERY' | 'SOME' | 'NONE' {
    let result: 'EVERY' | 'SOME' | 'NONE' | null = null;
    for (const row of rows) {
      if (isGridRowGroup(row)) {
        if (row.groupData) {
          if (rule(row.groupData)) {
            row.openRowCssClasses = (row.openRowCssClasses || '') + ' ' + cssClass;
            row.closedRowCssClasses = (row.closedRowCssClasses || '') + ' ' + cssClass;

            result = getNewResult('EVERY');
          } else {
            result = getNewResult('NONE');
          }
        }

        if (row.children) {
          const childResult = this.applyRowClassRule(cssClass, rule, row.children);

          if (!row.groupData) {
            if (childResult === 'EVERY') {
              row.openRowCssClasses = (row.openRowCssClasses || '') + ' ' + cssClass;
              row.closedRowCssClasses = (row.closedRowCssClasses || '') + ' ' + cssClass;

              result = getNewResult('EVERY');
            } else if (childResult === 'SOME') {
              row.closedRowCssClasses = (row.closedRowCssClasses || '') + ' ' + cssClass;

              result = getNewResult('SOME');
            }
          }
        }
      } else if (row.state === 'LOADED') {
        if (rule(row.data)) {
          row.rowCssClasses = (row.rowCssClasses || '') + ' ' + cssClass;

          result = getNewResult('EVERY');
        } else {
          result = getNewResult('NONE');
        }
      }
    }

    return result ?? 'NONE';

    function getNewResult(preferred: 'EVERY' | 'SOME' | 'NONE') {
      if (!result || result === preferred) {
        return preferred;
      }

      return 'SOME';
    }
  }
}
